import { TabPanel, TabView } from "primereact/tabview";
import Dashboard from "../Layout/Dashboard";
import { useParams } from "react-router-dom";
import FormSubHeading from "../components/FormSubHeading";
import { useForm, useStore } from "@tanstack/react-form";
import { Dropdown } from "primereact/dropdown";
import InputWithSuggestions from "../components/InputWithSuggestions";
import { InputText } from "primereact/inputtext";
import FieldInfo from "../components/forms/FieldInfo";
import { SelectButton } from "primereact/selectbutton";
import { CONDITIONS, GENDERS, PHONE_CODES } from "../constants";
import VSieteDateInput from "../components/VSieteDateInput";
import {
  useClientList,
  useSingleMedicalCase,
  useStateLocations,
  useSubsidiaries,
} from "../hooks/queries";
import { ProgressSpinner } from "primereact/progressspinner";
import { getHoldersMatchingCI, getPeopleMatchingCI } from "../services/people";
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Divider } from "primereact/divider";
import { getDependency, getHoldersRMSCI } from "../services/rms";
import { InputTextarea } from "primereact/inputtextarea";
import { Checkbox } from "primereact/checkbox";
import { MultiSelect } from "primereact/multiselect";
import { Button } from "primereact/button";
import { updateCase } from "../services/medicalCases";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { useDiagnosisListCached } from "../hooks/useDiagnosisListCached";

export default function EditCase() {
  const { caseId } = useParams();

  const { data, isLoading } = useSingleMedicalCase(caseId);

  const diagnosisQuery = useDiagnosisListCached();

  // Get affiliated clients
  const clientList = useClientList();

  // Get subsidiaries
  const subsidiariesQuery = useSubsidiaries();
  const stateLocationsQuery = useStateLocations();

  const [showDialog, setShowDialog] = useState(false);

  const initialPatientData = {
    ciType: data?.patient_.ci.slice(0, 1) || "",
    ci: data?.patient_.ci.slice(1) || "",
    name: data?.patient_.name || "",
    sex: data?.patient_.sex || "",
    birthDate: data?.patient_.birthdate.split("-").reverse().join("/") || "",
    condition: data?.patient_.condition || "",
    phoneCode: data?.patient_.phone.slice(0, 5) || "",
    phoneNumber: data?.patient_.phone.slice(5) || "",
    isMinor: data?.patient_.is_minor || false,
    minorId: data?.patient_.minor_id || "",
    diagnosis: data?.diagnosis.map((d) => String(d)) || [],
    client: data
      ? subsidiariesQuery.data?.find(
          (subsidiary) => subsidiary.id === data.patient_.subsidiary
        )?.company_client
      : null,
    subsidiary: data?.subsidiary || null,
    isHolder: data?.patient_.is_holder || false,
  };

  const initialHolderData = {
    ciType: data?.holder_.ci.slice(0, 1) || "",
    ci: data?.holder_.ci.slice(1) || "",
    name: data?.holder_.name || "",
    sex: data?.holder_.sex || "",
    birthDate: data?.holder_.birthdate.split("-").reverse().join("/") || "",
    condition: data?.holder_.condition || "",
    phoneCode: data?.holder_.phone.slice(0, 5) || "",
    phoneNumber: data?.holder_.phone.slice(5) || "",
    optionalPhoneCode: data?.holder_.optional_phone.slice(0, 5) || "",
    optionalPhoneNumber: data?.holder_.optional_phone.slice(5) || "",
    landlinePhoneNumber: data?.holder_.landline_phone || "",
    state: data?.state || null,
    city: data?.city || null,
    address: data?.address || "",
    patient: initialPatientData || "",
    prioritized: data?.prioritized || false,
  };

  const isVertice = data?.insurance_name === "Vértice Seguros";

  const toast = useRef(null);
  const queryClient = useQueryClient();

  const updateCaseMutation = useMutation({
    mutationFn: ({
      patient,
      holder,
      serviceNumber,
      prioritized,
      state = null,
      address = "",
    }) =>
      updateCase({
        caseId: data.id,
        patient: patient,
        holder: holder,
        serviceNumber,
        prioritized,
        state,
        address,
      }),
    onSuccess: ({ type, message, code, data }) => {
      if (type === "error") {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: message,
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: ["medicalCase", Number(caseId)],
        });
        toast.current.show({
          severity: "success",
          summary: "Éxito",
          detail: "Caso médico actualizado",
        });
        setShowDialog(false);
        holderSearchRef.current.hide();
      }
    },
  });

  const form = useForm({
    initialValues: initialHolderData,
    defaultValues: initialHolderData,
    validators: {
      onMount: ({ value }) => {
        return {
          fields: {
            ci: value.ci ? undefined : "Cédula es requerida",
          },
        };
      },
      onChange: ({ value }) => {
        if (value.step === 1) {
          const errorMap = {
            fields: {
              sex: value.sex ? undefined : "Seleccione un sexo",
              name: value.name ? undefined : "Nombre completo es requerido",
              birthDate:
                value.birthDate.length === 10
                  ? undefined
                  : "Fecha de nacimiento es requerida",
              condition: value.condition ? undefined : "Condicion es requerida",
              phoneNumber: value.phoneNumber
                ? undefined
                : "Número de teléfono es requerido",
              "patient.ci": undefined,
              "patient.name": undefined,
              "patient.sex": undefined,
              "patient.birthDate": undefined,
            },
          };
          if (value.landlinePhoneNumber) {
            if (
              !/^(02|2)/.test(value.landlinePhoneNumber) ||
              (value.landlinePhoneNumber.length !== 10 &&
                value.landlinePhoneNumber.length !== 11)
            ) {
              errorMap.fields.landlinePhoneNumber =
                "El teléfono fijo es inválido";
            }
          }
          return errorMap;
        }
        if (!value.patient.isHolder && value.step === 2) {
          return {
            fields: {
              "patient.ci":
                value.patient.ci || value.patient.ciType === "M"
                  ? undefined
                  : "Cédula es requerida",
              "patient.sex": value.patient.sex
                ? undefined
                : "Seleccione un sexo",
              "patient.name": value.patient.name
                ? undefined
                : "Nombre completo es requerido",
              "patient.birthDate":
                value.patient.birthDate.length === 10
                  ? undefined
                  : "Fecha de nacimiento es requerida",
            },
          };
        }
        if (value.patient.isHolder && value.step === 2) {
          return {
            fields: {
              "patient.ci": undefined,
              "patient.name": undefined,
              "patient.sex": undefined,
              "patient.birthDate": undefined,
            },
          };
        }
        return {
          fields: {
            sex: value.sex ? undefined : "Seleccione un sexo",
            name: value.name ? undefined : "Nombre completo es requerido",
            birthDate:
              value.birthDate.length === 10
                ? undefined
                : "Fecha de nacimiento es requerida",
            condition: value.condition ? undefined : "Condicion es requerida",
          },
        };
      },
    },
    onSubmit: async ({ value }) => {
      // Do something with form data
      const { patient, ...holder } = value;

      updateCaseMutation.mutate({
        patient: patient,
        holder: holder,
        serviceNumber: value.serviceNumber,
        prioritized: value.prioritized,
        state: value.state,
        address: value.address,
      });
    },
  });

  const holderSearchRef = useRef(null);
  const holderNameRef = useRef(null);

  const holderCi = useStore(form.store, (state) => state.values.ci);
  const holderCiType = useStore(form.store, (state) => state.values.ciType);
  const patientCi = useStore(form.store, (state) => state.values.patient.ci);
  const patientCiType = useStore(
    form.store,
    (state) => state.values.patient.ciType
  );
  const isHolder = useStore(
    form.store,
    (state) => state.values.patient.isHolder
  );
  const statePatient = useStore(form.store, (state) => state.values.state);

  /**
   * Sets form state from a selected patient
   * @param {API_Person} holder
   */
  const selectHolder = (holder) => {
    form.setFieldValue("ciType", holder.ci.slice(0, 1));
    form.setFieldValue("ci", holder.ci.slice(1));
    form.setFieldValue("name", holder.name);
    holderSearchRef.current.hide();
  };

  /**
   * Person search item template
   * @param {(item: API_Person) => void} onClick
   */
  function createSearchItemTemplate(onClick) {
    /** @param {API_Person} item */
    return (item) => (
      <>
        <span className="py-1" onClick={() => onClick(item)}>
          {item.ci} - {item.name}
        </span>
        <Divider />
      </>
    );
  }

  /**
   * Key up event for holder search
   * @param {KeyboardEvent} e
   */
  const onHolderSearchEnter = (e) => {
    if (e.key === "Escape") {
      holderSearchRef.current.hide();
    }
  };

  const [holderResult, setHolderResult] = useState([]);
  const [patientResult, setPatientResult] = useState([]);

  // Get patient by CI
  const patientQuery = useQuery({
    queryKey: ["patient", patientCiType + patientCi],
    queryFn: () => getPeopleMatchingCI(patientCiType + patientCi),
    enabled: patientCi.length >= 4,
  });

  // Get holder by CI in rms
  const holderRMSQuery = useQuery({
    queryKey: ["holder-rms", holderCiType + holderCi],
    queryFn: () => getHoldersRMSCI(holderCiType, holderCi),
    // enabled: holderForm.ci.length >= 4,
    enabled: false,
  });

  const holderQuery = useQuery({
    queryKey: ["holder", holderCiType + holderCi],
    queryFn: () => getHoldersMatchingCI(holderCiType + holderCi),
    // enabled: holderCi?.length >= 4,
    enabled: false,
  });

  const { data: dependencies } = useQuery({
    queryKey: ["patient-dependency", form.state.values.patient.minorId],
    queryFn: () =>
      getDependency({ dniCode: holderCiType, dniNumber: holderCi }),
    enabled: !!holderCi,
  });

  const dependencyList =
    dependencies?.results
      ?.filter((person) => person.correl_aseg !== 0)
      .map((item) => ({
        ...item,
        name: `${item.nombre}`,
        code: item.correl_aseg,
      })) || [];

  const dependency = [
    ...dependencyList,
    { name: "Otro", code: "00" }, // Nuevo ítem
  ];

  const search = async (e) => {
    if (e.query && /\D/.test(e.query)) {
      form.setFieldValue("ci", e.query.replace(/\D/g, ""));
      e.query = e.query.replace(/\D/g, "");
      search(e);
      return;
    }
    const response = await getHoldersMatchingCI(
      form.state.values.ciType + e.query
    );
    // const response = await getHoldersRMSCI(holderCiType, e.query);
    // const changeHolder = response.map((item) => ({
    //   ...item,
    //   label: `${item.cedrif_aseg} - ${item.nombre}`,
    //   value: item.cedrif_aseg,
    // }));
    const changeHolder = response.map((item) => ({
      ...item,
      label: `${item.ci} - ${item.name}`,
      value: item.ci.replace(/\D/g, ""),
    }));
    setHolderResult(changeHolder);
  };

  const searchPatient = async (e) => {
    if (e.query && /\D/.test(e.query)) {
      form.setFieldValue("patient.ci", e.query.replace(/\D/g, ""));
      e.query = e.query.replace(/\D/g, "");
      searchPatient(e);
      return;
    }
    const response = await getPeopleMatchingCI(
      form.state.values.patient.ciType + e.query
    );
    const changeHolder = response.map((item) => ({
      ...item,
      label: `${item.ci} - ${item.name}`,
      value: item.ci.replace(/\D/g, ""),
    }));
    setPatientResult(changeHolder);
  };

  const handlePhonePaste = (e, codeField, numberField) => {
    const text = e.clipboardData.getData("text/plain");
    const newText = text.replace(/\D/g, "");
    if (newText.length === 11) {
      // Incluye el 0
      const phoneCode = newText.slice(0, 4);
      const result = PHONE_CODES.find((item) => {
        return item.value === phoneCode || item.label === phoneCode;
      });

      if (result) {
        form.setFieldValue(codeField, result.value);
        form.setFieldValue(numberField, newText.slice(4));
      }
    } else if (newText.length === 10) {
      // No incluye el 0
      const phoneCode = "0" + newText.slice(0, 3);
      const result = PHONE_CODES.find((item) => {
        return item.value === phoneCode || item.label === phoneCode;
      });

      if (result) {
        form.setFieldValue(codeField, result.value);
        form.setFieldValue(numberField, newText.slice(3));
      }
    } else if (newText.length === 12) {
      // Empieza por 58
      const phoneCode = "0" + newText.slice(2, 5);
      const result = PHONE_CODES.find((item) => {
        return item.value === phoneCode || item.label === phoneCode;
      });

      if (result) {
        form.setFieldValue(codeField, result.value);
        form.setFieldValue(numberField, newText.slice(5));
      }
    }
    e.preventDefault();
  };

  return (
    <Dashboard headerTitle="Editar caso" dynamicGoBack>
      <Toast ref={toast} position="top-center" />
      <Dialog
        visible={showDialog}
        onHide={() => {
          if (showDialog) setShowDialog(false);
        }}
        header="Confirmar cambio"
        draggable={false}
      >
        <div className="flex flex-column align-items-center">
          <h3 className="mt-0">¿Está seguro de que desea editar el caso?</h3>
          <div className="flex justify-space-between gap-2">
            <Button
              severity="secondary"
              label="Cancelar"
              onClick={() => setShowDialog(false)}
            />
            <Button
              label="Guardar cambios"
              icon="pi pi-save"
              iconPos="right"
              onClick={() => {
                form.handleSubmit();
              }}
              disabled={updateCaseMutation.isPending}
              loading={updateCaseMutation.isPending}
            />
          </div>
        </div>
      </Dialog>
      <h3>
        <i className="pi pi-ticket"></i> Número de Orden: {caseId}
      </h3>
      {isLoading && !data ? (
        <ProgressSpinner />
      ) : (
        <form>
          <TabView id="edit-case-formg" panelContainerClassName="px-0">
            <TabPanel header="Editar titular">
              <div className="md:w-6">
                <FormSubHeading title="Datos del Titular" />
                <div className="p-field">
                  <label htmlFor="holderCi">Cédula</label>
                  <div className="field-with-select">
                    <form.Field name="ciType">
                      {(field) => (
                        <Dropdown
                          id={"holder" + field.name}
                          name={"holder" + field.name}
                          options={["V", "E"]}
                          placeholder="Tipo"
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.value)}
                          required
                        />
                      )}
                    </form.Field>

                    <form.Field name="ci">
                      {(field) => (
                        <InputWithSuggestions
                          inputName={field.name}
                          inputValue={field.state.value}
                          keyfilter="pint"
                          maxLength={9}
                          onCompleteMethod={search}
                          suggestions={holderResult}
                          onTextChange={(e) => {
                            if (e.value && e.value.hasOwnProperty("value")) {
                              field.handleChange(e.value.value);
                              form.setFieldValue("name", e.value.name);
                              form.setFieldValue("sex", e.value.sex);
                              form.setFieldValue(
                                "birthDate",
                                e.value.birthdate.split("-").reverse().join("/")
                              );
                              form.setFieldValue(
                                "condition",
                                e.value.condition
                              );
                              const phone = e.value.phone;
                              form.setFieldValue(
                                "phoneCode",
                                phone.slice(0, 5)
                              );
                              form.setFieldValue("phoneNumber", phone.slice(5));
                              // phone
                              form.validateSync();

                              return;
                            }
                            field.handleChange(e.value);
                          }}
                          query={holderRMSQuery}
                          notFoundBtnLabel="Crear registro de asegurado"
                          notFoundBtnClick={() => {}}
                          itemTemplate={createSearchItemTemplate(selectHolder)}
                          overlayRef={holderSearchRef}
                          onSearchEnter={onHolderSearchEnter}
                        />
                      )}
                    </form.Field>
                  </div>
                </div>

                <div className="p-field">
                  {/* Holder personal data */}
                  <form.Field
                    name="name"
                    children={(field) => (
                      <div className="p-field mt-2 flex flex-column">
                        <label htmlFor="holderName">Nombre completo</label>
                        <InputText
                          id="holderName"
                          name="holderName"
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          invalid={field.state.meta.errors.length > 0}
                          disabled={holderQuery.isLoading}
                          ref={holderNameRef}
                        />
                        <FieldInfo field={field} />
                      </div>
                    )}
                  />

                  {/* SEXO */}
                  <form.Field
                    name="sex"
                    children={(field) => (
                      <div className="p-field flex flex-column">
                        <label htmlFor="holderSex">Sexo</label>
                        <SelectButton
                          id="holderSex"
                          name="holderSex"
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          options={GENDERS}
                          disabled={holderQuery.isLoading}
                          invalid={field.state.meta.errors.length > 0}
                        />
                        <FieldInfo field={field} />
                      </div>
                    )}
                  />

                  <form.Field name="birthDate">
                    {(field) => (
                      <div className="p-field flex flex-column">
                        <span>Fecha de nacimiento</span>
                        <VSieteDateInput
                          name="holderBirthdate"
                          value={field.state.value}
                          setValue={(value) => field.handleChange(value)}
                          disabled={holderQuery.isLoading}
                          invalid={field.state.meta.errors.length > 0}
                        />
                      </div>
                    )}
                  </form.Field>

                  <form.Field name="condition">
                    {(field) => (
                      <div className="p-field flex flex-column">
                        <label htmlFor="holderCondition">Condición</label>
                        <SelectButton
                          id="holderCondition"
                          name="holderCondition"
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          options={CONDITIONS}
                          invalid={field.state.meta.errors.length > 0}
                        />
                      </div>
                    )}
                  </form.Field>

                  <div className="p-field">
                    <label htmlFor="phoneNumber">Número de teléfono</label>
                    <div className="field-with-select">
                      <form.Field name="phoneCode">
                        {(field) => (
                          <Dropdown
                            id="phoneCode"
                            name="phoneCode"
                            options={PHONE_CODES}
                            placeholder="Código"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.value)}
                            disabled={patientQuery.isLoading}
                          />
                        )}
                      </form.Field>

                      <form.Field name="phoneNumber">
                        {(field) => (
                          <InputText
                            id="phoneNumber"
                            name="phoneNumber"
                            inputMode="numeric"
                            keyfilter="pint"
                            maxLength={7}
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onPaste={(e) =>
                              handlePhonePaste(e, "phoneCode", "phoneNumber")
                            }
                            disabled={patientQuery.isLoading}
                          />
                        )}
                      </form.Field>
                    </div>
                  </div>

                  <div className="p-field">
                    <label htmlFor="optionalPhoneNumber">
                      Número de teléfono (opcional)
                    </label>
                    <div className="field-with-select">
                      <form.Field name="optionalPhoneCode">
                        {(field) => (
                          <Dropdown
                            id="phoneCode"
                            name="phoneCode"
                            options={PHONE_CODES}
                            placeholder="Código"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.value)}
                            disabled={patientQuery.isLoading}
                          />
                        )}
                      </form.Field>

                      <form.Field name="optionalPhoneNumber">
                        {(field) => (
                          <InputText
                            id="optionalPhoneNumber"
                            name="optionalPhoneNumber"
                            inputMode="numeric"
                            keyfilter="pint"
                            maxLength={7}
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onPaste={(e) =>
                              handlePhonePaste(
                                e,
                                "optionalPhoneCode",
                                "optionalPhoneNumber"
                              )
                            }
                            disabled={patientQuery.isLoading}
                          />
                        )}
                      </form.Field>
                    </div>
                  </div>

                  <div className="p-field flex flex-column">
                    <form.Field name="landlinePhoneNumber">
                      {(field) => (
                        <>
                          <label htmlFor="landlinePhoneNumber">
                            Número de teléfono fijo (opcional)
                          </label>
                          <InputText
                            id="landlinePhoneNumber"
                            name="landlinePhoneNumber"
                            inputMode="numeric"
                            keyfilter="pint"
                            maxLength={11}
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            invalid={field.state.meta.errors.length > 0}
                            onPaste={(e) => {
                              // Remove non-numeric values
                              const text =
                                e.clipboardData.getData("text/plain");
                              const newText = text.replace(/\D/g, "");
                              e.preventDefault();
                              field.handleChange(newText);
                            }}
                          />
                          <FieldInfo field={field} />
                        </>
                      )}
                    </form.Field>
                  </div>
                </div>
              </div>
            </TabPanel>
            <TabPanel header="Editar paciente">
              <div className="md:w-6">
                <FormSubHeading title="Datos del Paciente" />
                <form.Field name="patient.isHolder">
                  {(field) => (
                    <div className="flex flex-row align-items-center gap-3 pt-2 mb-2">
                      <span>¿El titular es el paciente?</span>
                      <SelectButton
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        options={[
                          { label: "Si", value: true },
                          { label: "No", value: false },
                        ]}
                        disabled={
                          patientQuery.isLoading || holderQuery.isLoading
                        }
                        allowEmpty={false}
                      />
                    </div>
                  )}
                </form.Field>
                {!isHolder && (
                  <>
                    <div className="p-field">
                      <label htmlFor="patientCi">Cédula</label>
                      <div className="field-with-select">
                        <form.Field name="patient.ciType">
                          {(field) => (
                            <Dropdown
                              id={"patient" + field.name}
                              name={"patient" + field.name}
                              options={[
                                { label: "V", value: "V" },
                                { label: "MENOR", value: "M" },
                              ]}
                              placeholder="Tipo"
                              value={field.state.value}
                              onChange={(e) => field.handleChange(e.value)}
                              required
                            />
                          )}
                        </form.Field>
                        {patientCiType !== "M" && (
                          <form.Field name="patient.ci">
                            {(field) => (
                              <InputWithSuggestions
                                inputName={field.name}
                                inputValue={field.state.value}
                                keyfilter="pint"
                                maxLength={9}
                                onCompleteMethod={searchPatient}
                                suggestions={patientResult}
                                onTextChange={(e) => {
                                  if (
                                    e.value &&
                                    e.value.hasOwnProperty("value")
                                  ) {
                                    field.handleChange(e.value.value);
                                    form.setFieldValue(
                                      "patient.name",
                                      e.value.name
                                    );
                                    form.setFieldValue(
                                      "patient.sex",
                                      e.value.sex
                                    );
                                    form.setFieldValue(
                                      "patient.birthDate",
                                      e.value.birthdate
                                        .split("-")
                                        .reverse()
                                        .join("/")
                                    );
                                    form.setFieldValue(
                                      "patient.condition",
                                      e.value.condition
                                    );
                                    const phone = e.value.phone;
                                    form.setFieldValue(
                                      "patient.phoneCode",
                                      phone.slice(0, 5)
                                    );
                                    form.setFieldValue(
                                      "patient.phoneNumber",
                                      phone.slice(5)
                                    );
                                    // phone
                                    form.validateSync();

                                    return;
                                  }
                                  field.handleChange(e.value);
                                }}
                                query={holderQuery}
                                notFoundBtnLabel="Crear registro de asegurado"
                                notFoundBtnClick={() => {}}
                                itemTemplate={createSearchItemTemplate(
                                  selectHolder
                                )}
                                overlayRef={holderSearchRef}
                                onSearchEnter={onHolderSearchEnter}
                              />
                            )}
                          </form.Field>
                        )}
                        {patientCiType === "M" && (
                          <form.Field name="patient.minorId">
                            {(field) => (
                              <Dropdown
                                id="minorId"
                                name="minorId"
                                options={dependency}
                                placeholder="Dependencia"
                                optionLabel="name"
                                optionValue="code"
                                value={field.state.value}
                                onChange={(e) => {
                                  field.handleChange(e.value);
                                  const dependencySelect = dependency.find(
                                    (item) => item.code === e.value
                                  );
                                  if (dependencySelect) {
                                    form.setFieldValue(
                                      "patient.name",
                                      dependencySelect.name
                                    );
                                  }
                                }}
                              />
                            )}
                          </form.Field>
                        )}
                      </div>
                    </div>
                    <form.Field
                      name="patient.name"
                      children={(field) => (
                        <div className="p-field mt-2 flex flex-column">
                          <label htmlFor="patientName">Nombre completo</label>
                          <InputText
                            id="patientName"
                            name="patientName"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            invalid={field.state.meta.errors.length > 0}
                            ref={holderNameRef}
                          />
                          <FieldInfo field={field} />
                        </div>
                      )}
                    />

                    <form.Field
                      name="patient.sex"
                      children={(field) => (
                        <div className="p-field flex flex-column">
                          <label htmlFor="patientSex">Sexo</label>
                          <SelectButton
                            id="patientSex"
                            name="patientSex"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            options={GENDERS}
                            invalid={field.state.meta.errors.length > 0}
                          />
                          <FieldInfo field={field} />
                        </div>
                      )}
                    />

                    <form.Field name="patient.birthDate">
                      {(field) => (
                        <div className="p-field flex flex-column">
                          <span>Fecha de nacimiento</span>
                          <VSieteDateInput
                            name="patientBirthdate"
                            value={field.state.value}
                            setValue={(value) => field.handleChange(value)}
                            invalid={field.state.meta.errors.length > 0}
                          />
                        </div>
                      )}
                    </form.Field>
                  </>
                )}
              </div>
            </TabPanel>
            <TabPanel header="Editar datos del caso">
              <div className="md:w-6">
                {/* SELECCIÓN DE DIAGNÓSTICO */}
                <FormSubHeading title="Diagnóstico" />
                <form.Field name="patient.diagnosis">
                  {(field) => (
                    <div className="p-field flex flex-column">
                      <label htmlFor="diagnosis">Diagnóstico</label>
                      <MultiSelect
                        id="diagnosis"
                        name="diagnosis"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.value)}
                        options={diagnosisQuery.data ?? []}
                        optionValue="id"
                        optionLabel="name"
                        placeholder="Seleccionar diagnóstico"
                        filter
                        loading={diagnosisQuery.isLoading}
                        disabled={diagnosisQuery.isLoading}
                        virtualScrollerOptions={{
                          itemSize: 48,
                          style: { width: "650px" },
                        }}
                        showSelectAll={false}
                        filterPlaceholder="Buscar"
                      />
                    </div>
                  )}
                </form.Field>

                <FormSubHeading title="Caso Priorizado" />
                <form.Field name="prioritized">
                  {(field) => (
                    <div className="p-field flex gap-2 align-items-center mb-2">
                      <Checkbox
                        inputId="prioritized"
                        name="prioritized"
                        checked={field.state.value}
                        onChange={(e) => field.handleChange(e.checked)}
                      />
                      <label htmlFor="prioritized">Priorizar caso</label>
                    </div>
                  )}
                </form.Field>

                {/* SELECCIÓN DE FILIAL */}
                {(!isVertice || (isVertice && clientList.data?.length > 1)) && (
                  <>
                    <FormSubHeading
                      title={isVertice ? "Colectivo" : "Filial"}
                    />
                    <form.Field name="patient.subsidiary">
                      {(field) => (
                        <div className="p-field flex flex-column">
                          <label htmlFor="subsidiary">
                            {isVertice ? "Colectivo" : "Filial"}
                          </label>
                          <Dropdown
                            id="subsidiary"
                            name="subsidiary"
                            options={subsidiariesQuery.data ?? []}
                            value={field.state.value}
                            onChange={(e) => {
                              field.handleChange(e.value);
                              form.setFieldValue(
                                "patient.client",
                                subsidiariesQuery.data.find(
                                  (subsidiary) => subsidiary.id === e.value
                                )?.company_client ?? null
                              );
                            }}
                            optionLabel="name"
                            optionValue="id"
                            placeholder={
                              isVertice
                                ? "Seleccionar colectivo"
                                : "Seleccionar filial"
                            }
                            filter
                            disabled={
                              patientQuery.isLoading ||
                              subsidiariesQuery.isLoading
                            }
                            loading={subsidiariesQuery.isLoading}
                            filterPlaceholder="Buscar"
                          />
                        </div>
                      )}
                    </form.Field>
                  </>
                )}

                {isVertice && (
                  <>
                    <FormSubHeading title="Estado" />
                    <form.Field name="state">
                      {(field) => (
                        <div className="p-field flex flex-column">
                          <label htmlFor="state">Estado</label>
                          <Dropdown
                            id="state"
                            name="state"
                            options={stateLocationsQuery.data ?? []}
                            value={field.state.value}
                            onChange={(e) => {
                              field.handleChange(e.value);
                              form.setFieldValue(
                                "patient.client",
                                subsidiariesQuery.data?.[0].company_client
                              );
                              form.setFieldValue(
                                "patient.subsidiary",
                                subsidiariesQuery.data?.[0].id
                              );
                            }}
                            optionLabel="name"
                            optionValue="id"
                            placeholder="Seleccionar estado"
                            filter
                            disabled={stateLocationsQuery.isLoading}
                            loading={stateLocationsQuery.isLoading}
                            filterPlaceholder="Buscar"
                          />
                        </div>
                      )}
                    </form.Field>
                    <form.Field name="city">
                      {(field) => (
                        <div className="p-field flex flex-column">
                          <label htmlFor="city">Ciudad</label>
                          <Dropdown
                            id="city"
                            name="city"
                            options={
                              statePatient
                                ? stateLocationsQuery.data?.find(
                                    (state) => state.id === statePatient
                                  )?.cities ?? []
                                : []
                            }
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.value)}
                            optionLabel="name"
                            optionValue="id"
                            placeholder="Seleccionar ciudad"
                            filter
                            disabled={
                              !statePatient || stateLocationsQuery.isLoading
                            }
                            loading={stateLocationsQuery.isLoading}
                            filterPlaceholder="Buscar"
                          />
                        </div>
                      )}
                    </form.Field>
                    <form.Field name="address">
                      {(field) => (
                        <div className="p-field flex flex-column">
                          <label htmlFor="address">Dirección (opcional)</label>
                          <InputTextarea
                            id="address"
                            name="address"
                            placeholder="Ejemplo: municipio, ciudad, calle, nombre del edificio, # de piso, # de casa"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            style={{ resize: "vertical" }}
                            rows={2}
                          />
                        </div>
                      )}
                    </form.Field>
                  </>
                )}
              </div>
            </TabPanel>
          </TabView>
          <Button
            type="button"
            icon="pi pi-save"
            label="Actualizar Caso"
            className="px-6 md:w-6 w-full"
            iconPos="right"
            loading={updateCaseMutation.isPending}
            disabled={updateCaseMutation.isPending}
            onClick={() => {
              setShowDialog(true);
            }}
          />
        </form>
      )}
    </Dashboard>
  );
}
